/**
 * MarqueeText - はみ出したテキストを自動スクロール表示する
 *
 * 収まりきる場合は何もせず、はみ出す場合のみ
 * 「左へスクロール → 停止 → 右へ戻る → 停止」をループする。
 */

import { useEffect, useRef } from 'react'

import { cn } from '@/lib/utils'

/** スクロール速度（px/秒） */
const SCROLL_SPEED = 30

/** 端で止まる時間（ミリ秒） */
const PAUSE_MS = 1500

type MarqueeTextProps = {
  text: string
  className?: string
}

export const MarqueeText = ({ text, className }: MarqueeTextProps) => {
  const containerRef = useRef<HTMLSpanElement>(null)
  const contentRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    const container = containerRef.current
    const content = contentRef.current
    if (!container || !content) return

    const overflow = content.scrollWidth - container.clientWidth
    if (overflow <= 0) return

    const travelMs = (overflow / SCROLL_SPEED) * 1000
    const totalMs = travelMs * 2 + PAUSE_MS * 2

    const animation = content.animate(
      [
        { transform: 'translateX(0)', offset: 0 },
        { transform: 'translateX(0)', offset: PAUSE_MS / totalMs },
        { transform: `translateX(-${overflow}px)`, offset: (PAUSE_MS + travelMs) / totalMs },
        { transform: `translateX(-${overflow}px)`, offset: (PAUSE_MS * 2 + travelMs) / totalMs },
        { transform: 'translateX(0)', offset: 1 },
      ],
      { duration: totalMs, iterations: Infinity, easing: 'ease-in-out' },
    )

    return () => animation.cancel()
  }, [text])

  return (
    <span ref={containerRef} className={cn('block overflow-hidden whitespace-nowrap', className)}>
      <span ref={contentRef} className="inline-block will-change-transform">
        {text}
      </span>
    </span>
  )
}
